import type { SealCategory } from '@areapif/areapif-shared'
import { isPhotoEvidenceStep, SEAL_CATEGORIES, sealCategoryToStepId, stepIdToPhotoCategory } from '@areapif/areapif-shared'
import {
  AddPhotoAlternate as AddPhotoIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as InspectionIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  EnhancedEncryption as AddSealIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material'
import { Box, Button, Card, CardContent, CardHeader, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { CircularProgress } from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import { useRedirect, useRefresh } from 'react-admin'

import type { EvidenceStepView } from '../../../domain/activeInspection'
import { buildEvidenceSteps, InspectionState } from '../../../domain/activeInspection'
import { NO_VALUE } from '../../../domain/activeInspectionCells'
import { nameEvidenceArchive } from '../../../domain/evidenceArchive'
import { useFileDownload } from '../../../hooks'
import { getTranslate, Locale } from '../../../i18n'
import { getRemoteUrl } from '../../../lib/supabase'
import type { HistoricalInspection } from '../../../models/inspection'
import { convertToInspection as convert } from '../../../models/inspection'
import { EvidenceStepStrip } from '../../atoms/EvidenceStepStrip'
import { ImageModal } from '../../atoms/ImageModal'
import { AddPhotosDialog } from '../../molecules/AddPhotosDialog'
import { AddSealsDialog } from '../../molecules/AddSealsDialog'
import { SoftDeleteButton } from '../../molecules/SoftDeleteButton'
import { detailsHeaderSx } from '../detailsHeader'
import { Timeline } from './Timeline'

export interface HistoricalInspectionDetailsProps {
  record: HistoricalInspection
  locale: Locale
}

interface BackButtonProps {
  locale: Locale
}

const BackButton: FC<BackButtonProps> = ({ locale }) => {
  const theme = useTheme()
  const redirect = useRedirect()
  const onClick = useCallback(() => {
    redirect('list', 'historical_inspections')
  }, [redirect])
  const t = getTranslate(locale)

  return (
    <Tooltip title={t('common.back')}>
      <IconButton
        onClick={onClick}
        size="medium"
        sx={{
          color: theme.palette.text.primary,
          mr: 1,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
        aria-label={t('common.back')}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  )
}

// Every photo and seal the record holds, named in the order the timeline renders
// them; evidence the timeline places on no step follows under sin_clasificar.
const getFilesToDownload = (record: HistoricalInspection, evidenceSteps: EvidenceStepView[]) =>
  nameEvidenceArchive(
    record.container,
    evidenceSteps,
    [...record.photos, ...record.seals].flatMap(evidence => (evidence.path ? [evidence.path] : [])),
  ).map(({ key, name }) => ({ name, url: getRemoteUrl('inspections', key) }))

const getDownloadZipFilename = (record: HistoricalInspection): string => {
  // An undated manual inspection archives with a NULL day, so the date is left
  // out of the filename rather than interpolated as "null".
  const dateString = record.day
  return `inspection_${record.container.replace(/\s+/g, '_')}${dateString ? `_${dateString}` : ''}.zip`
}

// Download button component
const DownloadAllButton: FC<{ locale: Locale; record: HistoricalInspection; evidenceSteps: EvidenceStepView[] }> = ({
  locale,
  record,
  evidenceSteps,
}) => {
  const { downloadFiles, downloadStatus } = useFileDownload()
  const t = getTranslate(locale)
  const isDownloading = downloadStatus === 'downloading'

  // Calculate if there are files to download
  const filesToDownload = useMemo(() => getFilesToDownload(record, evidenceSteps), [record, evidenceSteps])
  const hasFilesToDownload = useMemo(() => filesToDownload.length > 0, [filesToDownload])

  const handleDownload = useCallback(() => {
    void downloadFiles(filesToDownload, getDownloadZipFilename(record))
  }, [record, downloadFiles, filesToDownload])

  return (
    <Button
      color="primary"
      variant="text"
      size="small"
      onClick={handleDownload}
      disabled={isDownloading || !hasFilesToDownload}
      startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
      aria-label={t('pages.historical_inspections.download_all')}
    >
      {t('pages.historical_inspections.download_all')}
    </Button>
  )
}

interface SubheaderInfoProps {
  record: HistoricalInspection
  locale: Locale
}

const SubheaderInfo: FC<SubheaderInfoProps> = ({ record, locale }) => {
  const theme = useTheme()
  const t = getTranslate(locale)

  // An undated manual inspection archives with a NULL day, which renders as a
  // dash until SOSTRAT first matches the container and supplies the date.
  const day = record.day
  const formattedDate = day
    ? new Date(day).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : NO_VALUE

  return (
    <Typography variant="subtitle1" color="text.secondary" sx={{ ml: { xs: 0, sm: 6 } }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.light }} />
          {t(`resources.historical_inspections.enums.location.${record.location}`)}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.light }} />
          {formattedDate}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InspectionIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.light }} />
          {t(`resources.historical_inspections.enums.inspection_level.${record.inspection_level}`)}
        </Box>
      </Stack>
    </Typography>
  )
}

// Inspection header component
interface InspectionHeaderProps {
  record: HistoricalInspection
  evidenceSteps: EvidenceStepView[]
  locale: Locale
  onAddPhotos: () => void
  onAddSeals: () => void
}

const InspectionHeader: FC<InspectionHeaderProps> = ({ record, evidenceSteps, locale, onAddPhotos, onAddSeals }) => {
  const theme = useTheme()
  const t = getTranslate(locale)

  return (
    <CardHeader
      title={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BackButton locale={locale} />
          <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
            <Box component="span" sx={{ mr: 1 }}>
              {record.container}
            </Box>
            <EvidenceStepStrip steps={evidenceSteps} size={32} />
          </Typography>
        </Box>
      }
      subheader={<SubheaderInfo record={record} locale={locale} />}
      action={
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button color="primary" variant="text" size="small" onClick={onAddPhotos} startIcon={<AddPhotoIcon />}>
            {t('pages.historical_inspections.add_photos')}
          </Button>
          <Button color="primary" variant="text" size="small" onClick={onAddSeals} startIcon={<AddSealIcon />}>
            {t('pages.historical_inspections.add_seals')}
          </Button>
          <DownloadAllButton locale={locale} record={record} evidenceSteps={evidenceSteps} />
          <SoftDeleteButton record={record} redirectOnSuccess />
        </Stack>
      }
      sx={detailsHeaderSx(theme)}
    />
  )
}

// Timeline content component
interface TimelineContentProps {
  evidenceSteps: EvidenceStepView[]
  onImageClick: (imagePath: string) => void
  locale: Locale
}

const TimelineContent: FC<TimelineContentProps> = ({ evidenceSteps, onImageClick, locale }) => {
  const theme = useTheme()

  return (
    <CardContent sx={{ p: theme.spacing(3) }}>
      <Paper
        elevation={0}
        sx={{
          p: theme.spacing(2),
          bgcolor: theme.palette.background.default,
        }}
      >
        <Timeline steps={evidenceSteps} onImageClick={onImageClick} locale={locale} />
      </Paper>
    </CardContent>
  )
}

/**
 * HistoricalInspectionDetails component displays detailed inspection information
 * using shared library types and step-based galleries optimized for backoffice UX.
 */
export const HistoricalInspectionDetails: FC<HistoricalInspectionDetailsProps> = ({ record, locale }) => {
  const [modalImage, setModalImage] = useState<string | null>(null)
  const [photosDialogOpen, setPhotosDialogOpen] = useState(false)
  const [sealsDialogOpen, setSealsDialogOpen] = useState(false)
  const theme = useTheme()
  const refresh = useRefresh()

  // Convert historical inspection to InspectionDetails
  const inspection = useMemo(() => {
    try {
      const convertedInspection = convert(record)
      return convertedInspection
    } catch (_error) {
      // Return a minimal inspection object to prevent complete failure
      // Reuse the same adapter with the evidence stripped, so the degraded view
      // resolves the same flow as the happy path. Building the details here
      // instead would duplicate the factory's positional signature.
      return convert({ ...record, photos: [], seals: [] })
    }
  }, [record])

  // The same evidence state machine the tables render, read by both the header
  // strip and the galleries below it. An archived inspection is FINALIZADO by
  // definition, which is the state the strip needs in order to decide what a
  // step left without evidence reads as.
  const evidenceSteps = useMemo(() => buildEvidenceSteps(inspection.summaryView, InspectionState.FINISHED), [inspection])

  const availablePhotoCategories = useMemo(() => {
    const stepIds = inspection.summaryView.flatMap(phase => phase.steps.map(s => s.stepId))
    return stepIds.filter(isPhotoEvidenceStep).map(stepIdToPhotoCategory)
  }, [inspection])

  const availableSealCategories = useMemo(() => {
    const stepIds = new Set(inspection.summaryView.flatMap(phase => phase.steps.map(s => s.stepId)))
    return (Object.values(SEAL_CATEGORIES) as SealCategory[]).filter(cat => stepIds.has(sealCategoryToStepId(cat)))
  }, [inspection])

  const handleImageClick = useCallback(
    (imagePath: string) => {
      setModalImage(imagePath)
    },
    [setModalImage],
  )
  const handleCloseModal = useCallback(() => {
    setModalImage(null)
  }, [setModalImage])

  const handleOpenPhotosDialog = useCallback(() => {
    setPhotosDialogOpen(true)
  }, [])
  const handleClosePhotosDialog = useCallback(() => {
    setPhotosDialogOpen(false)
  }, [])
  const handleOpenSealsDialog = useCallback(() => {
    setSealsDialogOpen(true)
  }, [])
  const handleCloseSealsDialog = useCallback(() => {
    setSealsDialogOpen(false)
  }, [])

  const handleEvidenceSuccess = useCallback(() => {
    refresh()
  }, [refresh])

  return (
    <Box>
      <Card
        elevation={2}
        sx={{
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
          mb: theme.spacing(3),
        }}
      >
        <InspectionHeader
          record={record}
          evidenceSteps={evidenceSteps}
          locale={locale}
          onAddPhotos={handleOpenPhotosDialog}
          onAddSeals={handleOpenSealsDialog}
        />

        <TimelineContent evidenceSteps={evidenceSteps} onImageClick={handleImageClick} locale={locale} />

        <ImageModal open={!!modalImage} imagePath={modalImage} onClose={handleCloseModal} />
      </Card>

      <AddPhotosDialog
        open={photosDialogOpen}
        onClose={handleClosePhotosDialog}
        onSuccess={handleEvidenceSuccess}
        inspectionId={record.id}
        locale={locale}
        availableCategories={availablePhotoCategories}
      />

      <AddSealsDialog
        open={sealsDialogOpen}
        onClose={handleCloseSealsDialog}
        onSuccess={handleEvidenceSuccess}
        inspectionId={record.id}
        locale={locale}
        availableCategories={availableSealCategories}
      />
    </Box>
  )
}
